import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, ChevronLeft, ChevronRight, Loader2, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SLOT_IDS = ["morning", "afternoon", "evening"];
const SLOT_SHORT = { morning: "M", afternoon: "A", evening: "E" };
const SLOT_LABEL = { morning: "Morning", afternoon: "Afternoon", evening: "Evening" };
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Month calendar of bookings per day & slot, with admin lock/book controls.
export const EnquiryCalendar = ({ enquiries, capacity, onRefresh }) => {
  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState(null);
  const [locks, setLocks] = useState([]);
  const [busy, setBusy] = useState("");
  const [bookSlot, setBookSlot] = useState(null);
  const [bookForm, setBookForm] = useState({ name: "", phone: "", treatment: "" });

  const loadLocks = () => api.get("/admin/slots/locks").then((r) => setLocks(r.data || [])).catch(() => {});
  useEffect(() => {
    loadLocks();
  }, []);

  // date -> { morning: n, afternoon: n, evening: n }
  const byDate = useMemo(() => {
    const map = {};
    for (const e of enquiries) {
      const d = e.preferred_date;
      if (!d) continue;
      map[d] = map[d] || { morning: 0, afternoon: 0, evening: 0, items: [] };
      if (SLOT_IDS.includes(e.preferred_time)) map[d][e.preferred_time] += 1;
      map[d].items.push(e);
    }
    return map;
  }, [enquiries]);

  const lockedSet = useMemo(() => new Set(locks.map((l) => `${l.date}|${l.slot}`)), [locks]);
  const isLocked = (date, slot) => lockedSet.has(`${date}|all`) || lockedSet.has(`${date}|${slot}`);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayIso = iso(new Date());

  const toggleLock = async (date, slot) => {
    const key = `${date}|${slot}`;
    setBusy(key);
    try {
      if (lockedSet.has(key)) {
        await api.delete("/admin/slots/lock", { data: { date, slot } });
        toast.success(`${slot === "all" ? "Day" : SLOT_LABEL[slot]} unlocked`);
      } else {
        await api.post("/admin/slots/lock", { date, slot });
        toast.success(`${slot === "all" ? "Whole day" : SLOT_LABEL[slot]} locked — visitors can't book it now`);
      }
      await loadLocks();
    } catch {
      toast.error("Couldn't update the lock");
    } finally {
      setBusy("");
    }
  };

  const submitBooking = async () => {
    if (!bookForm.name.trim() || !bookForm.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setBusy("book");
    try {
      await api.post("/admin/enquiries", {
        ...bookForm,
        preferred_date: selected,
        preferred_time: bookSlot,
        notes: "Booked by admin",
      });
      toast.success(`Booked ${bookForm.name} — ${SLOT_LABEL[bookSlot]}, ${selected}`);
      setBookSlot(null);
      setBookForm({ name: "", phone: "", treatment: "" });
      onRefresh();
    } catch {
      toast.error("Booking failed");
    } finally {
      setBusy("");
    }
  };

  const dayBookings = selected ? byDate[selected]?.items || [] : [];

  return (
    <div data-testid="enquiry-calendar">
      {/* month nav */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          data-testid="calendar-prev-month"
          aria-label="Previous month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[#1A1A1A]/10 text-[#5A504B] transition-colors hover:border-[#D4AF37] hover:text-[#B38B2D]"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="font-serif text-lg font-semibold text-[#1A1615]" data-testid="calendar-month-label">
          {cursor.toLocaleString("en-GB", { month: "long", year: "numeric" })}
        </p>
        <button
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          data-testid="calendar-next-month"
          aria-label="Next month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[#1A1A1A]/10 text-[#5A504B] transition-colors hover:border-[#D4AF37] hover:text-[#B38B2D]"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* grid */}
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {WEEKDAYS.map((d) => (
          <p key={d} className="pb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-[#A39288]">{d}</p>
        ))}
        {[...Array(firstDow)].map((_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {[...Array(daysInMonth)].map((_, i) => {
          const date = iso(new Date(year, month, i + 1));
          const day = byDate[date];
          const allLocked = lockedSet.has(`${date}|all`);
          return (
            <button
              key={date}
              onClick={() => {
                setSelected(date);
                setBookSlot(null);
              }}
              data-testid={`calendar-day-${date}`}
              className={`min-h-[64px] rounded-xl border p-1.5 text-left align-top transition-all duration-200 ${
                selected === date
                  ? `border-[#D4AF37] shadow-[0_6px_18px_rgba(212,175,55,0.15)] ${allLocked ? "bg-red-50/70" : "bg-[#D4AF37]/10"}`
                  : allLocked
                    ? "border-red-200 bg-red-50/60"
                    : "border-[#1A1A1A]/8 bg-white/70 hover:border-[#D4AF37]/50"
              }`}
            >
              <span className={`text-xs font-medium ${date === todayIso ? "text-[#B38B2D]" : "text-[#1A1615]"}`}>{i + 1}</span>
              <span className="mt-1 flex flex-wrap gap-1">
                {SLOT_IDS.map((s) => {
                  const n = day?.[s] || 0;
                  const locked = isLocked(date, s);
                  if (!n && !locked) return null;
                  return (
                    <span
                      key={s}
                      className={`rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${
                        locked ? "bg-red-100 text-red-600" : n >= capacity ? "bg-[#1A1615] text-[#F3E5AB]" : "bg-[#D4AF37]/20 text-[#8a6d1f]"
                      }`}
                      title={`${SLOT_LABEL[s]}: ${locked ? "locked" : `${n}/${capacity}`}`}
                    >
                      {locked ? `${SLOT_SHORT[s]} 🔒` : `${SLOT_SHORT[s]} ${n}`}
                    </span>
                  );
                })}
              </span>
            </button>
          );
        })}
      </div>

      {/* day detail */}
      {selected && (
        <div className="glass mt-5 rounded-3xl p-6" data-testid="calendar-day-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-serif text-lg font-semibold text-[#1A1615]">
              {new Date(selected + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </h3>
            <button
              onClick={() => toggleLock(selected, "all")}
              disabled={busy === `${selected}|all`}
              data-testid="calendar-lock-day"
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                lockedSet.has(`${selected}|all`)
                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                  : "border border-[#1A1A1A]/10 text-[#5A504B] hover:border-red-300 hover:text-red-600"
              }`}
            >
              {lockedSet.has(`${selected}|all`) ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              {lockedSet.has(`${selected}|all`) ? "Unlock whole day" : "Lock whole day"}
            </button>
          </div>

          {/* slot rows */}
          <div className="mt-4 space-y-2">
            {SLOT_IDS.map((s) => {
              const n = byDate[selected]?.[s] || 0;
              const locked = isLocked(selected, s);
              const full = n >= capacity;
              return (
                <div key={s} className="flex flex-wrap items-center gap-3 rounded-xl bg-white/60 px-4 py-3" data-testid={`calendar-slot-row-${s}`}>
                  <span className="w-24 text-xs font-semibold text-[#1A1615]">{SLOT_LABEL[s]}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${locked ? "bg-red-100 text-red-600" : full ? "bg-[#1A1615] text-[#F3E5AB]" : "bg-[#D4AF37]/15 text-[#8a6d1f]"}`}>
                    {locked ? "Locked" : `${n}/${capacity}${full ? " · Full" : ""}`}
                  </span>
                  <span className="ml-auto flex gap-2">
                    <button
                      onClick={() => toggleLock(selected, s)}
                      disabled={busy === `${selected}|${s}` || lockedSet.has(`${selected}|all`)}
                      data-testid={`calendar-lock-${s}`}
                      className="flex items-center gap-1.5 rounded-full border border-[#1A1A1A]/10 px-3 py-1.5 text-[10px] font-medium text-[#5A504B] transition-colors hover:border-[#D4AF37] hover:text-[#B38B2D] disabled:opacity-40"
                    >
                      {locked ? <LockOpen className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                      {locked ? "Unlock" : "Lock"}
                    </button>
                    <button
                      onClick={() => setBookSlot(bookSlot === s ? null : s)}
                      disabled={locked}
                      data-testid={`calendar-book-${s}`}
                      className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-3 py-1.5 text-[10px] font-semibold text-[#1A1615] transition-opacity hover:opacity-90 disabled:opacity-40"
                    >
                      <CalendarPlus className="h-3 w-3" /> Book
                    </button>
                  </span>
                </div>
              );
            })}
          </div>

          {/* quick book form */}
          {bookSlot && (
            <div className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#FFFDF9] p-4" data-testid="calendar-book-form">
              <p className="text-xs font-semibold text-[#1A1615]">
                Book {SLOT_LABEL[bookSlot]} · {selected}
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <input
                  value={bookForm.name}
                  onChange={(e) => setBookForm({ ...bookForm, name: e.target.value })}
                  placeholder="Guest name *"
                  data-testid="calendar-book-name"
                  className="input-lux py-2.5 text-xs"
                />
                <input
                  value={bookForm.phone}
                  onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })}
                  placeholder="Phone *"
                  data-testid="calendar-book-phone"
                  className="input-lux py-2.5 text-xs"
                />
                <input
                  value={bookForm.treatment}
                  onChange={(e) => setBookForm({ ...bookForm, treatment: e.target.value })}
                  placeholder="Treatment (optional)"
                  data-testid="calendar-book-treatment"
                  className="input-lux py-2.5 text-xs"
                />
              </div>
              <button
                onClick={submitBooking}
                disabled={busy === "book"}
                data-testid="calendar-book-submit"
                className="btn-gold mt-3 px-5 py-2.5 text-xs disabled:opacity-60"
              >
                {busy === "book" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm booking"}
              </button>
            </div>
          )}

          {/* day's bookings */}
          <div className="mt-4 space-y-2">
            {dayBookings.length === 0 && <p className="text-xs font-light text-[#A39288]">No bookings this day yet.</p>}
            {dayBookings.map((e) => (
              <p key={e.id} className="text-xs font-light text-[#5A504B]" data-testid={`calendar-booking-${e.id}`}>
                <span className="font-medium text-[#1A1615]">{e.name}</span> — {e.treatment || "Consultation"}
                {e.preferred_time ? ` · ${e.preferred_time}` : ""} · {e.phone}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
